

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Product, B2BProduct, User } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { formatPrice } from '../config/currency';

interface ProductCardProps {
  product: Product;
  onOpenAddToCartModal: (product: Product) => void;
  isB2BView: boolean;
  user: User | null;
}

const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const StockBadge: React.FC<{ stock: number }> = ({ stock }) => {
    const { t } = useTranslation();
    if (stock <= 0) {
        return <span className="text-xs font-bold text-red-500 dark:text-red-400">{t('out_of_stock')}</span>;
    }
    if (stock < 10) {
        return <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{t('low_stock')}</span>;
    }
    return <span className="text-xs font-bold text-green-600 dark:text-green-400">{t('in_stock')}</span>;
};


const ProductCard: React.FC<ProductCardProps> = ({ product, onOpenAddToCartModal, isB2BView, user }) => {
    const { t } = useTranslation();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef<number | null>(null);
    
    const handleAddToCartClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.stock > 0) {
          onOpenAddToCartModal(product); // Call the modal opener
        }
    };

    const nextImage = useCallback((e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.imageUrls.length);
    }, [product.imageUrls.length]);

    const prevImage = useCallback((e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + product.imageUrls.length) % product.imageUrls.length);
    }, [product.imageUrls.length]);

    const resetImageTimer = useCallback(() => {
      if (timeoutRef.current) {
        // FIX: Explicitly use window.clearTimeout to ensure the browser's implementation is used, which expects a number.
        window.clearTimeout(timeoutRef.current);
      }
      if (!isHovered && product.imageUrls.length > 1) {
        const randomDelay = Math.random() * (5000 - 2000) + 2000; // 2 to 5 seconds
        // FIX: Explicitly use window.setTimeout to ensure the browser's implementation is used, which returns a number.
        timeoutRef.current = window.setTimeout(() => {
          setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.imageUrls.length);
        }, randomDelay);
      }
    }, [isHovered, product.imageUrls.length]);

    useEffect(() => {
      resetImageTimer();
      return () => {
        if (timeoutRef.current) {
          // FIX: Explicitly use window.clearTimeout for consistency.
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, [currentImageIndex, resetImageTimer]); // Trigger new timer when index changes or hover state changes

    const handleMouseEnter = () => {
      setIsHovered(true);
      // FIX: Explicitly use window.clearTimeout for consistency.
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setCurrentImageIndex(0); // Reset to first image on mouse leave
    };

    const priceGroup = user?.customerGroup || "Default";
    const displayPrice = product.prices[priceGroup];
    const priceSuffix = isB2BView ? ` / ${t('case')}` : '';

  return (
    <a href={`/#/products/${product.id}`} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col group transition-shadow duration-300 hover:shadow-xl no-underline">
      <div 
        className="relative h-48 w-full overflow-hidden" // Added overflow-hidden for sliding effect
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Wrapper for sliding images */}
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{
            width: `${product.imageUrls.length * 100}%`, // Make it wide enough for all images
            transform: `translateX(-${currentImageIndex * (100 / product.imageUrls.length)}%)`
          }}
        >
          {product.imageUrls.map((url, idx) => (
            <img
              key={idx}
              loading="lazy"
              className="h-full object-cover flex-shrink-0"
              style={{ width: `calc(100% / ${product.imageUrls.length})` }} // Dynamic width for each image
              src={url}
              alt={`${product.name} - ${idx + 1}`}
            />
          ))}
        </div>
        <div className="absolute top-0 right-0 bg-brand-green-light text-brand-dark text-xs font-bold px-3 py-1 m-2 rounded-full">{product.type}</div>

        {isHovered && product.imageUrls.length > 1 && (
            <>
                <button 
                    onClick={prevImage}
                    className="absolute top-1/2 left-2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                    aria-label={t('pagination_prev')}
                >
                    <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <button 
                    onClick={nextImage}
                    className="absolute top-1/2 right-2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                    aria-label={t('pagination_next')}
                >
                    <ArrowRightIcon className="w-5 h-5" />
                </button>
                <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-1">
                    {product.imageUrls.map((_, idx) => (
                        <span key={idx} className={`block w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-brand-green' : 'bg-white opacity-50'}`}></span>
                    ))}
                </div>
            </>
        )}
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-brand-dark dark:text-white mb-2">{product.name}</h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm flex-grow">{product.description}</p>
        <div className="mt-4 text-xs text-slate-500 dark:text-slate-400 space-y-1">
            {isB2BView && 'caseSize' in product ? (
              <>
                <p><strong className="dark:text-slate-300">{t('b2b_case_size')}:</strong> {(product as B2BProduct).caseSize} packs</p>
                <p><strong className="dark:text-slate-300">Total:</strong> {product.packSize * (product as B2BProduct).caseSize} {t('b2b_rolls_in_case')}</p>
              </>
            ) : (
              <>
                <p><strong className="dark:text-slate-300">{t('pack_size')}:</strong> {product.packSize} {t('rolls')}</p>
                <p><strong className="dark:text-slate-300">{t('ply')}:</strong> {product.ply}</p>
                <p><strong className="dark:text-slate-300">{t('sheets')}:</strong> {product.sheetsPerRoll}{t('sheets_per_roll')}</p>
              </>
            )}
        </div>
        <div className="mt-auto pt-5">
          <div className="flex items-baseline justify-between">
            <p className="text-2xl font-extrabold text-brand-green dark:text-brand-green">
              {formatPrice(displayPrice)}
              <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-1">{priceSuffix}</span>
            </p>
            <StockBadge stock={product.stock} />
          </div>
          <button 
              onClick={handleAddToCartClick}
              disabled={product.stock <= 0}
              className="mt-4 w-full bg-brand-dark text-white font-bold py-2 px-4 rounded-lg shadow-md group-hover:bg-brand-green dark:text-white transition-colors duration-300 disabled:bg-slate-400 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
          >
            {product.stock > 0 ? t('add_to_cart_btn') : t('out_of_stock')}
          </button>
        </div>
      </div>
    </a>
  );
};

export default ProductCard;